import { useTranslation } from 'react-i18next';

// Domain
import {
  getFieldId,
  getFieldLib,
  getFieldHelp,
  getFieldType,
  getFieldValidation,
  getFieldLabelBold,
  getFieldLabelColor,
  getFieldLabelItalic,
  isLiveSearchEnabledField,
} from '../../../domain/form/formUseCase';
import { encodeFieldName } from '../../../domain/form/fieldNameCodec';

// Styles
import { Popover } from '../../../store/popover/PopoverContext';
import Picto from '../Picto/Picto';
import { PopoverContent, PopoverTrigger } from '../Popover/Popover';
import styles from './FormFieldWrapper.module.css';
import cn from '../../../utils/cn';

/**
 * Field wrapper with label, help and error display
 * @import { FieldValues } from 'react-hook-form';
 * @typedef {import('react-hook-form').FieldValues} FieldValues
 * @param {object} props - Component props
 * @param {FormItem} props.field - Form item (discriminated union)
 * @param {FieldValues} [props.error] - Field error object
 * @param {JSX.Element} props.children - Children element
 * @returns {JSX.Element} Field wrapper
 */
function FieldWrapper({
  field, error, children,
}) {
  const { t } = useTranslation();
  const fieldId = getFieldId(field);
  const fieldLib = getFieldLib(field);
  const fieldHelp = getFieldHelp(field);
  const fieldValidation = getFieldValidation(field);
  const labelBold = getFieldLabelBold(field);
  const labelItalic = getFieldLabelItalic(field);
  const labelColor = getFieldLabelColor(field);

  if (!fieldId) return null;

  const fieldIdAttr = `field-${encodeFieldName(fieldId)}`;
  const errorId = error ? `${fieldIdAttr}-error` : undefined;
  const fieldType = getFieldType(field);
  // Checkbox and radio render their own label next to the control
  const rendersOwnLabel = fieldType === 'checkbox' || fieldType === 'radio';
  const isFullWidth = fieldType === 'mergeTable' || fieldType === 'grid';
  const hideFieldError = fieldType === 'grid';

  return (
    <div
      className={cn([
        styles['form-field'],
        isFullWidth && styles['full-width'],
        error && styles['has-error'],
      ])}
    >
      {fieldLib && !rendersOwnLabel && (
        <label
          htmlFor={fieldIdAttr}
          className={cn([
            styles['form-label'],
            isLiveSearchEnabledField(field) && styles['form-label-live-search'],
            labelBold && styles['form-label-bold'],
            labelItalic && styles['form-label-italic'],
          ])}
          style={labelColor ? { color: labelColor } : undefined}
        >
          {fieldLib}
          {fieldValidation?.required && (
            <span className={styles['form-required']} aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      <div className={styles['form-field-content']}>
        {children}

        {fieldHelp ? (
          <Popover placement="right" isOnHover>
            {/* Out of the tab order: Tab goes straight to the next field */}
            <PopoverTrigger className={styles['form-help-trigger']} tabIndex={-1}>
              <Picto icon="message-question" className={styles['form-help-icon']} />
            </PopoverTrigger>
            <PopoverContent className={styles['form-help-popover']}>
              <div className={styles['form-help']}>
                {fieldHelp}
              </div>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>

      {!hideFieldError && error && (
        <div id={errorId} className={styles['form-error']} role="alert" aria-live="polite">
          {t(error.message)}
        </div>
      )}
    </div>
  );
}

export default FieldWrapper;
